import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import { paginationDropdownOptions } from 'constants/dropdownOptions';
import { Tag } from 'primereact/tag';
import DiscrepancyFilter from './DiscrepancyFilter';
import { handlePageNumberInPagination } from 'common/pagination';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';
import GenericFilePreviewModal from 'components/common/GenericFilePreviewModal';
import useAutomatedTime from 'hooks/useAutomatedTime';

export const DiscrepancyHistory = ({
  isCaseFileManager,
  isLeadFileManager,
  caseDetails,
  listDiscrepancies,
  loadDiscrepancies,
  refreshDiscrepancies,
}) => {
  const userContext = useUserDetailsContext();
  const { onMountUnMount, onOperationBreak } = useAutomatedTime(caseDetails?.case_id, caseDetails?.case_name);

  const getPageLimit = () => {
    if (isCaseFileManager && userContext) {
      return userContext?.firmDetails?.pagination_preference?.case?.discrepancies;
    } else if (isLeadFileManager && userContext) {
      return userContext?.firmDetails?.pagination_preference?.lead?.discrepancies;
    } else if (userContext) {
      return userContext?.firmDetails?.pagination_preference?.discrepancies?.self;
    }
  };

  const [isForward, setIsForward] = useState(true);
  const [pageLimit, setPageLimit] = useState(getPageLimit() ?? 10);
  const [searchKey, setSearchKey] = useState('');
  const [nextToken, setNextToken] = useState();
  const [transitionToken, setTransitionToken] = useState();

  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const loadDiscrepanciesWithTokens = async (limit = 10, token = '', direction = 'forward', sk = '', searchKey = '', case_id = '') => {
    const result = await loadDiscrepancies(limit, token, direction, sk, searchKey, case_id);
    if (result && result?.data?.tasks) {
      setNextToken(result.data.tasks?.next_token);
      setTransitionToken(result.data.tasks?.transition_token);
    }
  };

  const handleViewReport = (rowData) => {
    if (rowData?.file_path) {
      const fileData = {
        file_path: rowData.file_path,
        display_name: rowData.display_name || 'Discrepancy Report',
        file_name: rowData.display_name || 'Discrepancy Report',
        version_id: rowData.version_id,
        sk: rowData.sk,
        pk: rowData.pk,
        ...rowData,
      };
      setPreviewFile(fileData);
      setShowPreview(true);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewFile(null);
  };

  const getFilePathForPagination = () => {
    if (isCaseFileManager && userContext) {
      return { module: 'case', subModule: '', listName: 'discrepancies' };
    } else if (isLeadFileManager && userContext) {
      return { module: 'leads', subModule: '', listName: 'discrepancies' };
    } else {
      return { module: 'discrepancies', subModule: '', listName: 'self' };
    }
  };

  const onNextPageClick = () => {
    loadDiscrepanciesWithTokens(
      pageLimit,
      !isForward ? transitionToken : nextToken,
      'forward',
      '',
      searchKey,
      isCaseFileManager || isLeadFileManager ? caseDetails?.case_id : ''
    );
    setIsForward(true);
  };
  const onPrevPageClick = () => {
    loadDiscrepanciesWithTokens(
      pageLimit,
      isForward ? transitionToken : nextToken,
      'reverse',
      '',
      searchKey,
      isCaseFileManager || isLeadFileManager ? caseDetails?.case_id : ''
    );
    setIsForward(false);
  };

  const onPageChange = (event) => {
    setPageLimit(event.value);
    let path = getFilePathForPagination();
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, event?.value);
    loadDiscrepanciesWithTokens(
      event?.value,
      '',
      'forward',
      '',
      searchKey,
      isCaseFileManager || isLeadFileManager ? caseDetails?.case_id : ''
    );
    setIsForward(true);
  };

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: (options) => {
      return (
        <React.Fragment>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Item Per Page
          </span>
          <Dropdown value={options.value} options={paginationDropdownOptions} onChange={onPageChange} />
        </React.Fragment>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button
          onClick={onNextPageClick}
          disabled={nextToken === null && isForward}
          className={'p-paginator-next p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button
          onClick={onPrevPageClick}
          disabled={transitionToken === null || (transitionToken && nextToken === null && !isForward)}
          className={'p-paginator-next p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
  };

  const timestampTemplate = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Created On</span>
        <span
          className="text-break"
          title={
            convertDateFromApiToUserTimezone(rowData?.time, userContext?.userDetails?.timezone, constants.month_date_year_time_12_format) ??
            null
          }
        >
          {convertDateFromApiToUserTimezone(rowData?.time, userContext?.userDetails?.timezone, constants.month_date_year_time_12_format) ||
            '-'}
        </span>
      </React.Fragment>
    );
  };

  const filesTemplate = (rowData) => {
    const files = rowData?.file_details || {};
    const fileNames = Object.keys(files);
    const sanitizedSk = rowData?.sk.replace(/[^a-zA-Z0-9_-]/g, '_');
    const displayedFiles = fileNames.slice(0, 1);
    const remainingFiles = fileNames.slice(1);

    return (
      <React.Fragment>
        <span className="p-column-title text-break">Files</span>
        <React.Fragment>
          <span className="text-break d-block">
            <span className="file-manager-ellipsis-text" title={displayedFiles}>
              {displayedFiles}
            </span>

            {remainingFiles.length > 0 && (
              <div>
                <Tag className={`p-mr-2 pa-tags more-tag more-tag-${sanitizedSk}`} rounded value={`+${remainingFiles.length} more`} />
                <Tooltip target={`.more-tag-${sanitizedSk}`} position="top" className="pa-tooltip discrepancy-files-tooltip">
                  {remainingFiles.map((files, index) => (
                    <span key={index}>
                      {files}
                      {index < remainingFiles?.length - 1 && <>{','}&nbsp;</>}
                    </span>
                  ))}
                </Tooltip>
              </div>
            )}
          </span>
        </React.Fragment>
      </React.Fragment>
    );
  };

  const statusTemplate = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Status</span>
        {rowData?.task_status === 'Success' ? (
          <>
            <div>
              <Tag className="tag-case s-tag d-status-tags" value={rowData?.task_status}></Tag>
            </div>
          </>
        ) : rowData?.task_status === 'Failure' ? (
          <>
            <div>
              <Tag className="tag-case da-tag d-status-tags" value={rowData?.task_status}></Tag>
            </div>
          </>
        ) : rowData?.task_status === 'In Progress' ? (
          <>
            <div>
              <Tag className="tag-case y-tag d-status-tags" value={rowData?.task_status}></Tag>
            </div>
          </>
        ) : (
          <Tag className="tag-case bg-light" value="-"></Tag>
        )}
      </React.Fragment>
    );
  };

  const actionBody = (rowData) => {
    const isDisabled = rowData?.task_status !== 'Success';

    return (
      <React.Fragment>
        <span className="p-column-title text-break">Action</span>
        <i
          id={`preview-${rowData?.sk.replace(/[^a-zA-Z0-9_-]/g, '_')}`}
          className={`bi bi-box-arrow-up-right mx-2 mt-1 pointer ${isDisabled ? 'text-muted' : 'text-primary-main'}`}
          style={isDisabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}
          onClick={() => {
            if (!isDisabled) {
              handleViewReport(rowData);
            }
          }}
        ></i>
        <Tooltip target={`#preview-${rowData?.sk.replace(/[^a-zA-Z0-9_-]/g, '_')}`} position="top">
          {isDisabled ? '' : 'View Report'}
        </Tooltip>
      </React.Fragment>
    );
  };

  useEffect(() => {
    loadDiscrepanciesWithTokens(
      pageLimit,
      '',
      'forward',
      '',
      searchKey,
      isCaseFileManager || isLeadFileManager ? caseDetails?.case_id : ''
    );
  }, [searchKey]);

  return (
    <>
      <DiscrepancyFilter
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        refreshDiscrepancies={refreshDiscrepancies}
        limit={pageLimit}
        id={caseDetails?.case_id}
      />
      <div className="col-12 p-0 datatable-responsive card-bg discrepancy-history-table">
        <DataTable
          value={listDiscrepancies}
          className="p-datatable-responsive"
          paginator
          paginatorTemplate={paginatorTemplate}
          rows={pageLimit}
        >
          <Column header="Created On" field="task_name" body={timestampTemplate} bodyClassName="ellipsis-text discrepancy-createdOn" />
          <Column header="Files" field="files" body={filesTemplate} />
          <Column header="Status" field="task_status" body={statusTemplate} bodyClassName="ellipsis-text" />
          <Column header="Actions" body={actionBody} />
        </DataTable>
      </div>

      <GenericFilePreviewModal
        visible={showPreview}
        onHide={handleClosePreview}
        file={previewFile}
        title="Discrepancy Report Preview"
        enableActivityLog={true}
        onMountUnMount={onMountUnMount}
        onOperationBreak={onOperationBreak}
        caseDetails={caseDetails}
      />
    </>
  );
};
